/* ============================================================
   SONDER, app.js
   Self-contained interactions. No external deps required.
   Shopify AJAX cart is feature-detected (works on a real store;
   falls back to a local mock in the static /preview build).
   ============================================================ */
(function () {
  "use strict";
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const money = (cents, cur = "$") =>
    cur + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 });

  /* ---------- Scroll reveals ---------- */
  function initReveal() {
    const targets = $$("[data-reveal], [data-reveal-stagger], .blurfade, .clip-line");
    const noanim = /[?&]noanim/.test(location.search); // QA/screenshot mode
    const show = (el) => {
      if (el.classList.contains("is-in")) return;
      el.classList.add("is-in");
      if (el.hasAttribute("data-reveal-stagger")) {
        $$(":scope > *", el).forEach((child, ci) => {
          child.style.transitionDelay = ci * 70 + "ms";
        });
      }
    };
    if (reduce || noanim || !("IntersectionObserver" in window)) {
      targets.forEach(show);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          show(e.target);
          io.unobserve(e.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );
    targets.forEach((t) => io.observe(t));
    // The bottom rootMargin creates a dead zone for content already on screen
    // at load (e.g. hero copy at the foot of a 100svh hero), reveal anything
    // in the initial viewport immediately instead of waiting for a scroll.
    setTimeout(() => {
      targets.forEach((t) => {
        const r = t.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          show(t);
          io.unobserve(t);
        }
      });
    }, 60);
  }

  /* ---------- Split text into clip-reveal lines ---------- */
  function initSplit() {
    $$("[data-split]").forEach((el) => {
      const text = el.textContent.trim();
      el.innerHTML = "";
      const span = document.createElement("span");
      span.textContent = text;
      el.appendChild(span);
      el.classList.add("clip-line");
    });
  }

  /* ---------- Velocity-driven motion blur ---------- */
  function initMotionBlur() {
    if (reduce) return;
    const els = $$("[data-vblur]");
    if (!els.length) return;
    let lastY = window.scrollY,
      vel = 0,
      raf;
    function loop() {
      const y = window.scrollY;
      const dv = Math.min(Math.abs(y - lastY) * 0.18, 8);
      vel += (dv - vel) * 0.25;
      lastY = y;
      els.forEach((el) => (el.style.filter = `blur(${vel.toFixed(2)}px)`));
      raf = requestAnimationFrame(loop);
    }
    loop();
  }

  /* ---------- Header hide-on-scroll ---------- */
  function initHeader() {
    const header = $("[data-header]");
    if (!header) return;
    let last = 0;
    window.addEventListener(
      "scroll",
      () => {
        const y = window.scrollY;
        header.dataset.hidden = y > 240 && y > last ? "true" : "false";
        last = y;
      },
      { passive: true }
    );
  }

  /* ---------- Mobile nav ---------- */
  function initMobileNav() {
    const nav = $("[data-mobile-nav]");
    const open = $("[data-nav-open]");
    const close = $("[data-nav-close]");
    if (!nav) return;
    const set = (v) => {
      nav.dataset.open = v;
      document.body.style.overflow = v === "true" ? "hidden" : "";
    };
    open && open.addEventListener("click", () => set("true"));
    close && close.addEventListener("click", () => set("false"));
    $$("a", nav).forEach((a) => a.addEventListener("click", () => set("false")));
  }

  /* ---------- Accordions ---------- */
  function initAccordion() {
    $$("[data-acc]").forEach((acc) => {
      $$(".acc__head", acc).forEach((head) => {
        head.addEventListener("click", () => {
          const item = head.closest(".acc__item");
          const isOpen = item.dataset.open === "true";
          if (!acc.hasAttribute("data-multi")) {
            $$(".acc__item", acc).forEach((i) => (i.dataset.open = "false"));
          }
          item.dataset.open = isOpen ? "false" : "true";
        });
      });
    });
  }

  /* ---------- Product gallery ---------- */
  function initGallery() {
    const g = $("[data-gallery]");
    if (!g) return;
    const main = $(".gallery__main", g);
    $$(".thumb", g).forEach((thumb) => {
      thumb.addEventListener("click", () => {
        $$(".thumb", g).forEach((t) => t.setAttribute("aria-current", "false"));
        thumb.setAttribute("aria-current", "true");
        const vid = thumb.dataset.video;
        const src = thumb.dataset.src;
        const cls = thumb.dataset.cls || "";
        if (vid && main) {
          if (reduce) {
            main.innerHTML = `<img class="pack-thumb" src="${thumb.dataset.poster || ""}" alt="">`;
          } else {
            main.innerHTML = `<video autoplay muted loop playsinline poster="${thumb.dataset.poster || ""}"><source src="${vid}" type="video/webm"></video>`;
          }
        } else if (src && main) main.innerHTML = `<img class="${cls}" src="${src}" alt="">`;
      });
    });
  }

  /* ---------- Purchase modes (one-time / subscribe) ---------- */
  function initModes() {
    $$("[data-modes]").forEach((group) => {
      $$(".mode", group).forEach((mode) => {
        mode.addEventListener("click", () => {
          $$(".mode", group).forEach((m) => (m.dataset.active = "false"));
          mode.dataset.active = "true";
          const sel = group.dataset.selling;
          if (sel) {
            const input = $(sel);
            if (input) input.value = mode.dataset.value || "";
          }
          const form = group.closest("form");
          const priceLabel = form && form.querySelector("[data-atc-price]");
          if (priceLabel && mode.dataset.price) priceLabel.textContent = mode.dataset.price;
        });
      });
    });
  }

  /* ---------- Qty steppers ---------- */
  function initQty() {
    $$("[data-qty]").forEach((q) => {
      const input = $("input", q);
      $$("button", q).forEach((b) =>
        b.addEventListener("click", () => {
          const dir = b.dataset.dir === "up" ? 1 : -1;
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + dir);
        })
      );
    });
  }

  /* ============================================================
     CART
     ============================================================ */
  const SHOPIFY = typeof window.Shopify !== "undefined";
  const Cart = {
    open() { const d = $("[data-cart-drawer]"); if (d) d.dataset.open = "true"; document.body.style.overflow = "hidden"; },
    close() { const d = $("[data-cart-drawer]"); if (d) d.dataset.open = "false"; document.body.style.overflow = ""; },
    async add(id, qty, props) {
      if (SHOPIFY) {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, quantity: qty, properties: props || {} }),
        });
        if (!res.ok) throw new Error("add failed");
        const item = await res.json().catch(() => ({}));
        const cart = await this.refresh();
        document.dispatchEvent(new CustomEvent("sonder:cart-add", {
          detail: Object.assign({}, item, {
            item_names: cart && cart.items ? cart.items.map((i) => i.product_title) : [],
          }),
        }));
      } else {
        mockCart.add(id, qty);
        this.renderMock();
      }
      this.open();
    },
    async change(key, qty) {
      if (SHOPIFY) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        await this.refresh();
      } else {
        mockCart.change(key, qty);
        this.renderMock();
      }
    },
    async refresh() {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      this.render(cart);
      return cart;
    },
    render(cart) {
      const count = $("[data-cart-count]");
      if (count) { count.textContent = cart.item_count; count.style.display = cart.item_count ? "" : "none"; }
      const items = $("[data-cart-items]");
      const total = $("[data-cart-total]");
      if (total) total.textContent = money(cart.total_price);
      if (!items) return;
      if (!cart.item_count) { items.innerHTML = `<p class="cart-empty">your cart is empty</p>`; return; }
      items.innerHTML = cart.items
        .map(
          (it) => `
        <div class="cart-line" data-key="${it.key}">
          <div class="cart-line__img">${it.image ? `<img src="${it.image}" alt="">` : '<div class="field"></div>'}</div>
          <div>
            <div class="cart-line__title">${it.product_title}</div>
            <div class="cart-line__meta">${it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : "stick pack"}</div>
            <div class="qty mt-1" data-cart-qty>
              <button data-key="${it.key}" data-to="${it.quantity - 1}">–</button>
              <input value="${it.quantity}" readonly>
              <button data-key="${it.key}" data-to="${it.quantity + 1}">+</button>
            </div>
          </div>
          <div class="cart-line__meta">${money(it.final_line_price)}</div>
        </div>`
        )
        .join("");
      this.bindLines();
    },
    bindLines() {
      $$("[data-cart-qty] button", $("[data-cart-items]")).forEach((b) =>
        b.addEventListener("click", () => this.change(b.dataset.key, parseInt(b.dataset.to, 10)))
      );
    },
    renderMock() { this.render(mockCart.toShopify()); },
  };

  /* mock cart for the static preview build */
  const MOCK_IMG = "../assets/sonder-sachet-front.png";
  const MOCK_CATALOG = {
    "mock-focus-1m": { variant: "one-month · 25 packets", price: 7900, image: MOCK_IMG },
    "mock-focus-4m": { variant: "four-month · 100 packets", price: 28500, image: MOCK_IMG },
  };
  const mockCart = {
    lines: {},
    add(id, qty) { this.lines[id] = (this.lines[id] || 0) + qty; },
    change(key, qty) { if (qty <= 0) delete this.lines[key]; else this.lines[key] = qty; },
    toShopify() {
      const items = Object.entries(this.lines).map(([id, q]) => {
        const p = MOCK_CATALOG[id] || { variant: "25 packets", price: 7900, image: MOCK_IMG };
        return {
          key: id, id, product_title: "Sonder Work Mode", variant_title: p.variant,
          quantity: q, final_line_price: p.price * q, image: p.image || null,
        };
      });
      const item_count = items.reduce((a, b) => a + b.quantity, 0);
      const total_price = items.reduce((a, b) => a + b.final_line_price, 0);
      return { item_count, total_price, items };
    },
  };

  function initCart() {
    $$("[data-cart-open]").forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); Cart.open(); }));
    $$("[data-cart-close], [data-cart-scrim]").forEach((b) => b.addEventListener("click", () => Cart.close()));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") Cart.close(); });

    $$("[data-add-to-cart]").forEach((form) => {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const btn = $('[type="submit"]', form);
        const label = btn ? btn.querySelector("span") : null;
        const old = label ? label.textContent : "";
        if (label) label.textContent = "adding…";
        try {
          const id = (form.querySelector('[name="id"]') || {}).value || "mock-focus";
          const qty = parseInt((form.querySelector('[name="quantity"]') || {}).value || "1", 10);
          await Cart.add(id, qty);
        } catch (err) {
          if (label) label.textContent = "try again";
          console.error(err);
          return;
        }
        if (label) label.textContent = old;
      });
    });

    if (SHOPIFY) Cart.refresh().catch(() => {});
    else Cart.renderMock();
  }

  /* ---------- Preloader: blur-to-focus, once per session ---------- */
  function initPreloader() {
    const pl = $("[data-preloader]");
    if (!pl) return;
    const seen = (() => { try { return sessionStorage.getItem("sonder-pl"); } catch (e) { return "1"; } })();
    if (reduce || seen || /[?&]nopl/.test(location.search)) { pl.remove(); return; }

    document.body.style.overflow = "hidden";
    pl.dataset.state = "run";

    const finish = () => {
      if (pl.dataset.state === "done") return;
      pl.dataset.state = "done";
      document.body.style.overflow = "";
      try { sessionStorage.setItem("sonder-pl", "1"); } catch (e) {}
      setTimeout(() => pl.remove(), 900);
    };

    const minDwell = new Promise((r) => setTimeout(r, 1400));
    const loaded = document.readyState === "complete"
      ? Promise.resolve()
      : new Promise((r) => window.addEventListener("load", r, { once: true }));
    Promise.all([minDwell, loaded]).then(finish);
    setTimeout(finish, 4000); // safety: never hold the page hostage
  }

  /* ---------- Autoplay nudge: retry stalled loop videos (Safari low-power, bg tabs) ---------- */
  function initAutoplayNudge() {
    if (reduce) return;
    const tryPlay = () => $$("video[autoplay]").forEach((v) => { if (v.paused) v.play().catch(() => {}); });
    document.addEventListener("visibilitychange", tryPlay);
    window.addEventListener("scroll", tryPlay, { passive: true });
    document.addEventListener("pointerdown", tryPlay, { once: true });
    tryPlay();
  }

  /* ---------- Reduced motion: swap autoplay videos for their posters ---------- */
  function initMotionMedia() {
    if (!reduce) return;
    $$("video[autoplay]").forEach((v) => {
      const poster = v.getAttribute("poster");
      if (poster) {
        const img = document.createElement("img");
        img.src = poster;
        img.alt = v.getAttribute("aria-label") || "";
        img.className = "pack-thumb";
        v.replaceWith(img);
      } else {
        v.removeAttribute("autoplay");
        v.pause();
      }
    });
  }

  /* ---------- Video testimonials: click opens a YouTube lightbox ---------- */
  function initVideoLightbox() {
    const open = (el) => {
      const src = el.dataset.video;
      const media = src
        ? '<video src="' + src + '" poster="' + (el.dataset.poster || "") + '" controls autoplay playsinline></video>'
        : '<iframe src="https://www.youtube-nocookie.com/embed/' + el.dataset.yt +
          '?autoplay=1&playsinline=1&rel=0&modestbranding=1" title="Sonder review" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
      const box = document.createElement("div");
      box.className = "ytbox";
      box.innerHTML = '<button class="ytbox__close">[ close ]</button><div class="ytbox__panel">' + media + '</div>';
      document.body.appendChild(box);
      document.body.style.overflow = "hidden";
      const close = () => {
        box.remove();
        document.body.style.overflow = "";
        document.removeEventListener("keydown", esc);
      };
      const esc = (e) => { if (e.key === "Escape") close(); };
      box.addEventListener("click", (e) => {
        if (e.target === box || e.target.closest(".ytbox__close")) close();
      });
      document.addEventListener("keydown", esc);
    };
    $$("[data-yt], [data-video]").forEach((el) =>
      el.addEventListener("click", (e) => {
        e.preventDefault();
        open(el);
      })
    );
  }

  /* ---------- Notify me: scroll to footer + focus the email input ---------- */
  function initNotify() {
    $$('a[href$="#footer"]').forEach((a) =>
      a.addEventListener("click", () => {
        setTimeout(() => {
          const input = $(".newsletter input");
          if (input) input.focus({ preventScroll: true });
        }, 700);
      })
    );
  }

  /* ---------- Contact page: copy email, reason chips, transmission ---------- */
  function initContact() {
    $$("[data-copy]").forEach((b) =>
      b.addEventListener("click", async () => {
        try { await navigator.clipboard.writeText(b.dataset.copy); } catch (e) {}
        const label = b.querySelector("[data-copy-label]") || b;
        const prev = label.textContent;
        b.dataset.copied = "true";
        label.textContent = "copied";
        setTimeout(() => { b.dataset.copied = "false"; label.textContent = prev; }, 1500);
      })
    );

    $$("[data-chips]").forEach((group) => {
      const subj = group.dataset.subject ? $(group.dataset.subject) : null;
      $$(".chip", group).forEach((chip) =>
        chip.addEventListener("click", () => {
          $$(".chip", group).forEach((c) => (c.dataset.active = "false"));
          chip.dataset.active = "true";
          if (subj) subj.value = chip.dataset.val || chip.textContent.trim();
        })
      );
    });

    const form = $("[data-contact-form]");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const body = form.closest(".term__body") || form.parentNode;
        const tx = $(".transmit", body);
        form.style.display = "none";
        if (!tx) return;
        tx.classList.add("is-on");
        $$(".transmit__line", tx).forEach((l, i) =>
          setTimeout(() => l.classList.add("is-in"), 250 + i * 450)
        );
      });
    }
  }

  /* ---------- Journal: category filter ---------- */
  function initJournalFilter() {
    const filter = $("[data-jfilter]");
    const search = $("[data-jsearch]");
    if (!filter && !search) return;
    const cards = $$("[data-jcard]");
    const empty = $("[data-jempty]");
    let cat = "all", q = "";
    const apply = () => {
      cards.forEach((c) => {
        const okCat = cat === "all" || c.dataset.cat === cat;
        const okQ = !q || (c.textContent || "").toLowerCase().includes(q);
        c.hidden = !(okCat && okQ);
      });
      if (empty) empty.hidden = cards.some((c) => !c.hidden);
    };
    if (filter) {
      $$(".jpill", filter).forEach((p) =>
        p.addEventListener("click", () => {
          $$(".jpill", filter).forEach((x) => (x.dataset.active = "false"));
          p.dataset.active = "true";
          cat = p.dataset.cat;
          apply();
        })
      );
    }
    if (search) {
      search.addEventListener("input", () => { q = search.value.trim().toLowerCase(); apply(); });
    }
  }

  /* ---------- Article: scroll progress bar + TOC scroll-spy ---------- */
  function initArticleProgress() {
    const bar = $("[data-progress]");
    const links = $$("[data-toc] a");
    if (!bar && !links.length) return;
    const heads = links.map((a) => $(a.getAttribute("href"))).filter(Boolean);
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-h")) || 68;
    const upd = () => {
      const h = document.documentElement;
      if (bar) {
        const max = h.scrollHeight - h.clientHeight;
        bar.style.width = (max > 0 ? (h.scrollTop / max) * 100 : 0) + "%";
      }
      if (heads.length) {
        let idx = 0;
        heads.forEach((el, i) => { if (el.getBoundingClientRect().top - headerH - 60 <= 0) idx = i; });
        links.forEach((a, i) => a.setAttribute("aria-current", i === idx ? "true" : "false"));
      }
    };
    window.addEventListener("scroll", upd, { passive: true });
    upd();
  }

  /* ---------- Help center: live search filter ---------- */
  function initHelpSearch() {
    const input = $("[data-hcsearch]");
    if (!input) return;
    const cards = $$("[data-hccard]");
    const empty = $("[data-hcempty]");
    input.addEventListener("input", () => {
      const q = input.value.trim().toLowerCase();
      let shown = 0;
      cards.forEach((c) => {
        const hay = (c.textContent + " " + (c.dataset.kw || "")).toLowerCase();
        const hit = !q || hay.includes(q);
        c.hidden = !hit;
        if (hit) shown++;
      });
      if (empty) empty.hidden = shown !== 0;
    });
  }

  /* ---------- Help: predictive search dropdown ---------- */
  const MOCK_HELP_INDEX = [
    { u: "help-cancel-order.html", t: "cancel an order", k: "Help" },
    { u: "help-product.html", t: "product questions", k: "Help" },
    { u: "help-shipping.html", t: "shipping & tracking", k: "Help" },
    { u: "help-returns.html", t: "returns & refunds", k: "Help" },
    { u: "help-account.html", t: "accounts & subscriptions", k: "Help" },
    { u: "help-affiliate.html", t: "affiliate & partnerships", k: "Help" },
    { u: "journal-article.html", t: "the 9-hour rule: timing caffeine for sleep", k: "Article" },
    { u: "faq.html", t: "frequently asked questions", k: "Page" },
  ];
  function initPredictiveSearch() {
    const wrap = $("[data-predictive]");
    if (!wrap) return;
    const input = $("[data-hcsearch]", wrap);
    const box = $("[data-predictive-results]", wrap);
    if (!input || !box) return;
    const item = (i) =>
      `<a href="${i.u}"><span class="hc-suggest__k">${i.k}</span><span class="hc-suggest__t">${i.t}</span></a>`;
    const show = (items) => {
      box.innerHTML = items.length
        ? items.slice(0, 8).map(item).join("")
        : '<div class="hc-suggest__empty">No matches. Press enter to search everything.</div>';
      box.hidden = false;
    };
    const live = typeof window.Shopify !== "undefined";
    let t;
    input.addEventListener("input", () => {
      const q = input.value.trim();
      clearTimeout(t);
      if (q.length < 2) { box.hidden = true; return; }
      if (live) {
        t = setTimeout(() => {
          const root = (window.Shopify.routes && window.Shopify.routes.root) || "/";
          fetch(root + "search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=article,page&resources[limit]=8")
            .then((r) => r.json())
            .then((d) => {
              const r = d.resources.results;
              const items = []
                .concat((r.articles || []).map((a) => ({ u: a.url, t: a.title, k: "Article" })))
                .concat((r.pages || []).map((p) => ({ u: p.url, t: p.title, k: "Page" })));
              show(items);
            })
            .catch(() => { box.hidden = true; });
        }, 200);
      } else {
        const ql = q.toLowerCase();
        show(MOCK_HELP_INDEX.filter((i) => i.t.toLowerCase().includes(ql)));
      }
    });
    input.addEventListener("keydown", (e) => { if (e.key === "Escape") box.hidden = true; });
    document.addEventListener("click", (e) => { if (!wrap.contains(e.target)) box.hidden = true; });
  }

  /* ---------- About: cursor-following row preview ---------- */
  function initRowPeek() {
    const peek = $("[data-rowpeek]");
    if (!peek || reduce || window.matchMedia("(hover: none)").matches) return;
    const img = $("img", peek);
    const wrap = peek.closest("[data-rows-peek]") || document;
    const rows = $$("[data-hoverimg]", wrap);
    rows.forEach((r) => { new Image().src = r.dataset.hoverimg; }); // preload
    let tx = 0, ty = 0, x = 0, y = 0, active = false;
    const loop = () => {
      x += (tx - x) * 0.16; y += (ty - y) * 0.16;
      peek.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${active ? 1 : 0.9})`;
      requestAnimationFrame(loop);
    };
    loop();
    const container = rows.length ? rows[0].closest(".rows-peek") : null;
    rows.forEach((r) => {
      r.addEventListener("mouseenter", (e) => {
        tx = x = e.clientX; ty = y = e.clientY;
        img.src = r.dataset.hoverimg;
        active = true; peek.classList.add("is-on");
        if (container) container.classList.add("is-hovering");
      });
      r.addEventListener("mousemove", (e) => { tx = e.clientX; ty = e.clientY; });
      r.addEventListener("mouseleave", () => {
        active = false; peek.classList.remove("is-on");
        if (container) container.classList.remove("is-hovering");
      });
    });
  }

  /* ---------- Liquid-fill headings (berry tide on hover, any text) ---------- */
  let lfN = 0;
  function initLiquidFill() {
    if (reduce) return;
    const NS = "http://www.w3.org/2000/svg";
    const wave = (amp) => {
      let d = "M-200,0 ";
      for (let x = -200; x < 1600; x += 200) d += "c33," + -amp + " 67," + -amp + " 100,0 c33," + amp + " 67," + amp + " 100,0 ";
      return d + "L1600,900 L-200,900 Z";
    };
    $$("[data-liquidfill]").forEach((el) => {
      const text = el.textContent.trim();
      if (!text || el.querySelector(".lf-svg")) return;
      const cs = getComputedStyle(el);
      const fs = parseFloat(cs.fontSize);
      const mk = () => {
        const t = document.createElementNS(NS, "text");
        t.setAttribute("x", "0"); t.setAttribute("y", "0");
        t.setAttribute("font-family", cs.fontFamily);
        t.setAttribute("font-weight", cs.fontWeight);
        t.setAttribute("font-size", fs);
        t.setAttribute("letter-spacing", cs.letterSpacing);
        t.textContent = text;
        return t;
      };
      const msvg = document.createElementNS(NS, "svg");
      msvg.style.cssText = "position:absolute;visibility:hidden;left:-9999px";
      const mt = mk(); msvg.appendChild(mt); document.body.appendChild(msvg);
      const bb = mt.getBBox(); document.body.removeChild(msvg);
      if (!bb.width) return;
      const pad = fs * 0.16;
      const x = bb.x - pad, y = bb.y - pad, w = bb.width + pad * 2, h = bb.height + pad * 2;
      const id = "lf" + ++lfN;
      const svg = document.createElementNS(NS, "svg");
      svg.setAttribute("class", "lf-svg");
      svg.setAttribute("viewBox", x + " " + y + " " + w + " " + h);
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      svg.style.width = w + "px";
      svg.innerHTML =
        '<defs><clipPath id="' + id + '">' + mk().outerHTML + "</clipPath></defs>" +
        '<g clip-path="url(#' + id + ')">' +
        '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="' + cs.color + '"/>' +
        '<g class="lf-liquid" style="--lf-rest:' + (y + h + 40) + "px;--lf-hover:" + (y - 40) + 'px">' +
        '<path class="lf-wave2" fill="#a31f52" d="' + wave(h * 0.16) + '"/>' +
        '<path class="lf-wave" fill="#cb2a63" d="' + wave(h * 0.11) + '"/>' +
        "</g></g>";
      el.textContent = "";
      el.classList.add("lf-host");
      el.style.filter = "none";
      el.appendChild(svg);
    });
  }

  /* ---------- Year stamp ---------- */
  function initYear() { $$("[data-year]").forEach((e) => (e.textContent = new Date().getFullYear())); }

  /* ---------- Boot ---------- */
  function boot() {
    document.documentElement.classList.remove("no-js");
    initPreloader();
    initSplit();
    initReveal();
    initMotionBlur();
    initHeader();
    initMobileNav();
    initAccordion();
    initGallery();
    initMotionMedia();
    initAutoplayNudge();
    initModes();
    initQty();
    initCart();
    initVideoLightbox();
    initContact();
    initJournalFilter();
    initArticleProgress();
    initHelpSearch();
    initPredictiveSearch();
    initRowPeek();
    initNotify();
    initLiquidFill();
    initYear();
    document.documentElement.classList.add("js-ready");
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
